// ---------------------------------------------------------------------------
// Classification engine (blueprint v2 §4, task C-1) — CLIENT copy.
// Pure function, zero React imports (§2 contract types via @/types/planData).
// Server mirror: server/src/features/construction-plans/classificationEngine.ts.
//
// A10.48-2023 §4.4 classification: worst-case of all triggered rules.
// Z11 (Brian field ruling): jump/redirect blocks ON STRUCTURE get NO QE
// advisory — classification is purely rule-driven. The only arrangement-level
// special case is a redirect/rigging attached to an APPURTENANCE (IV(e)),
// which may be deemed Class III via the §4.4.4.1 exception path.
// ---------------------------------------------------------------------------

import type {
  ConstructionClass,
  ClassificationResult,
  ClassificationTrigger,
} from '@/types/planData';

export type { ConstructionClass, ClassificationResult, ClassificationTrigger };

export interface ExceptionConditions {
  goodCondition: boolean;
  within4ft: boolean;
  max200lb: boolean;
  proofTest: boolean;
}

export interface ClassifierAnswers {
  q1_involvesStructure: boolean;
  q2_hoisting: boolean;
  q3_normalMethods: boolean;          // false ⇒ IV(b)/(c)/(d)
  q4_attachedToStructure: boolean;    // false ⇒ III(d) detached device
  q5_attachmentLocation: 'legPanelPointOrShaft' | 'mount' | 'mastPipeRoosterHead' | 'other';
  q6_exceptionConditions: ExceptionConditions | null;
  q7_grossLoadLbs: number | null;
  q8_geometry: { loadPositionDeg: number | null; horizontalTagDeg: number | null }; // PULLED from lift details, never re-asked
  q9_criticalLift: boolean;
  q10_newStructureConcerns: boolean | null;
}

/** Structural subset of LiftDetail the classifier reads (§2 contract). */
export interface ClassifierLiftDetail {
  loading?: { grossLoadLbs?: number };
  geometry?: {
    topBlock?: { attachment?: ClassifierAttachment };
    redirectBlock?: { attachment?: ClassifierAttachment } | null;
    mountBlock?: { attachment?: ClassifierAttachment } | null;
    highestAttachmentFt?: number;
    tag?: { method?: string };
  };
}

interface ClassifierAttachment {
  method?: string;
  slingEnds?: [string, string];
}

export interface ClassifyOverride {
  selected: ConstructionClass;
  by: string;
  justification?: string;
  timestamp?: string;
}

export interface ClassifyInput {
  answers: ClassifierAnswers;
  liftDetails?: ClassifierLiftDetail[];
  override?: ClassifyOverride | null;
  now?: string;
}

/** Thrown when a downgrade override lacks the mandatory justification (§4 override rule). */
export class ClassificationOverrideError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClassificationOverrideError';
  }
}

const CLASS_RANK: Record<ConstructionClass, number> = { II: 2, III: 3, IV: 4 };

const NO_CONDITIONS: ExceptionConditions = {
  goodCondition: false,
  within4ft: false,
  max200lb: false,
  proofTest: false,
};

/** Required personnel per class (§4 output card). */
export function requiredPersonnelFor(cls: ConstructionClass): string[] {
  switch (cls) {
    case 'II':
      return ['competent person', 'qualified person + onsite competent person (alternative)'];
    case 'III':
      return ['qualified person', 'onsite competent person(s)'];
    case 'IV':
      return ['qualified person', 'onsite competent person(s)', 'qualified engineer (ANSI/TIA-322-A evaluation)'];
  }
}

/**
 * IV(e) trigger check for a sling-end set (Brian's verified correction):
 * IV(e) fires on WHERE sling ends attach — a face-basket whose ends terminate
 * at vertical tower leg panel points (or the main pole shaft) does NOT trigger.
 * Any end NOT at a panel point/shaft triggers IV(e).
 */
function slingEndTriggersIVe(ends: [string, string] | undefined): boolean {
  if (!ends) return false;
  return ends.some((end) => end !== 'legPanelPoint' && end !== 'poleShaft');
}

/** Attachment method triggers IV(e) (appurtenance/mount/mast-pipe/rooster head). */
function attachmentTriggersIVe(attachment: ClassifierAttachment | undefined): boolean {
  if (!attachment) return false;
  const method = attachment.method;
  if (!method) return false;
  if (method === 'faceBasket2') return slingEndTriggersIVe(attachment.slingEnds);
  return method !== 'legPanelPoint' && method !== 'poleShaft';
}

export function classify(input: ClassifyInput): ClassificationResult {
  const { answers, liftDetails = [], override = null, now = new Date().toISOString() } = input;
  const triggers: ClassificationTrigger[] = [];

  let recommended: ConstructionClass = 'II';
  const bump = (cls: ConstructionClass) => {
    if (CLASS_RANK[cls] > CLASS_RANK[recommended]) recommended = cls;
  };

  let exceptionInvoked: '4.4.4.1(a)' | '4.4.4.1(b)' | null = null;
  const exceptionConditions: ExceptionConditions = answers.q6_exceptionConditions
    ? { ...answers.q6_exceptionConditions }
    : { ...NO_CONDITIONS };
  let qeEngaged = false;

  // Rule 1 — Q1 no ⇒ out of §4.4 scope; recommended II with scope note.
  if (!answers.q1_involvesStructure) {
    triggers.push({
      rule: 'scope',
      detail: 'No plan required by §4.4 — work does not involve the structure or its foundations; other documentation may apply.',
    });
  } else {
    // Rule 2 — Q3 no ⇒ IV (triggers IV(b)/(c)/(d) as applicable) + QE banner.
    if (!answers.q3_normalMethods) {
      bump('IV');
      triggers.push({
        rule: 'IV(b)',
        detail: 'Scope involves structural member work / modification / special engineered method — not normal construction methods (§4.4.4).',
      });
      qeEngaged = true;
    }

    // Rule 8 — Q10 concerns ⇒ IV(c); none ⇒ III(c).
    if (answers.q10_newStructureConcerns === true) {
      bump('IV');
      triggers.push({ rule: 'IV(c)', detail: 'New structure/foundation work with strength or stability concerns identified (§4.4.4).' });
      qeEngaged = true;
    } else if (answers.q10_newStructureConcerns === false) {
      bump('III');
      triggers.push({ rule: 'III(c)', detail: 'New structure/foundation work; no strength or stability concerns identified.' });
    }

    // Rule 7 — criticalLift ⇒ force IV (⚑ BRIAN DEFAULT — conservative).
    if (answers.q9_criticalLift) {
      bump('IV');
      triggers.push({
        rule: 'IV',
        detail: 'Critical lift designated — Class IV (Conformara proposed default, conservative pending standard-text verification).',
      });
      qeEngaged = true;
    }

    // Rule 3 — Q4 detached ⇒ base III (III(d)); STILL evaluate IV(e)/(f).
    if (!answers.q4_attachedToStructure) {
      bump('III');
      triggers.push({ rule: 'III(d)', detail: 'Detached lifting device (e.g., crane) — not attached to the structure (§4.4.4).' });
    }

    // Rule 4 — IV(e): attachment away from primary structure (answers + per-lift attachments).
    const iveSources: string[] = [];
    if (answers.q4_attachedToStructure && answers.q5_attachmentLocation !== 'legPanelPointOrShaft') {
      iveSources.push(answers.q5_attachmentLocation === 'mount' ? 'rigging attached away from leg panel points / pole shaft — antenna mount ⇒ IV(e)' : answers.q5_attachmentLocation === 'mastPipeRoosterHead' ? 'rigging attached away from leg panel points / pole shaft — mast pipe / rooster head (drop-in gin pole) ⇒ IV(e)' : 'rigging attached away from vertical leg panel points / main pole shaft (appurtenance, mount, or other member)');
    }
    for (const lift of liftDetails) {
      const g = lift.geometry;
      if (!g) continue;
      if (attachmentTriggersIVe(g.topBlock?.attachment)) {
        iveSources.push(`${liftLabel(lift)}: top block attachment not at a leg panel point / pole shaft`);
      }
      if (attachmentTriggersIVe(g.mountBlock?.attachment)) {
        iveSources.push(`${liftLabel(lift)}: mount block attachment not at a leg panel point / pole shaft`);
      }
      if (attachmentTriggersIVe(g.redirectBlock?.attachment)) {
        iveSources.push(`${liftLabel(lift)}: redirect block on an appurtenance (NOT on the primary structure)`);
      }
    }
    if (iveSources.length > 0) {
      // §4.4.4.1(b) exception path: all four conditions true ⇒ may be deemed Class III.
      const conditions = answers.q6_exceptionConditions;
      const allTrue =
        !!conditions &&
        conditions.goodCondition && conditions.within4ft && conditions.max200lb && conditions.proofTest;
      if (allTrue) {
        bump('III');
        exceptionInvoked = '4.4.4.1(b)';
        triggers.push({
          rule: 'IV(e)-exception',
          detail: `May be deemed Class III by the qualified person — all four §4.4.4.1(b) conditions maintained and documented (${iveSources.join('; ')}).`,
        });
      } else {
        bump('IV');
        exceptionInvoked = '4.4.4.1(b)';
        qeEngaged = true;
        triggers.push({
          rule: 'IV(e)',
          detail: `${iveSources.join('; ')} — engage QE per ANSI/TIA-322-A (§4.4.4.1) unless all four exception conditions are maintained and documented.`,
        });
      }
    }

    // Rule 5 — weight bands (attached lifts).
    const gross = answers.q7_grossLoadLbs;
    if (answers.q2_hoisting && gross !== null) {
      if (gross <= 35) {
        triggers.push({
          rule: 'II(c)',
          detail: `Handline load ${formatLbs(gross)} ≤ 35 lb — need not be documented (informational note only; Conformara proposed default).`,
        });
        bump('II');
      } else if (gross <= 500) {
        bump('II');
        triggers.push({ rule: 'II(b)', detail: `Gross load ${formatLbs(gross)} ≤ 500 lb — Class II band (§4.4.3).` });
      } else if (gross <= 2000) {
        bump('III');
        triggers.push({ rule: 'III(b)', detail: `Gross load ${formatLbs(gross)} > 500 lb and ≤ 2,000 lb — Class III band (§4.4.4).` });
      } else {
        bump('IV');
        qeEngaged = true;
        triggers.push({ rule: 'IV(a)', detail: `Gross load ${formatLbs(gross)} > 2,000 lb — Class IV (§4.4.4).` });
      }
    }

    // Rule 6 — geometry IV(f): straight tag with θ > 10° OR horizontal tag > 70°.
    // Exactly 10/70 stays Class III ("exceeding" per verified text). Pulled from
    // lift details / answers, never re-asked.
    const straightTagGeometry =
      answers.q2_hoisting &&
      (liftDetails.length === 0 || liftDetails.some((l) => (l.geometry?.tag?.method ?? 'straight') === 'straight'));
    if (straightTagGeometry) {
      const theta = answers.q8_geometry.loadPositionDeg;
      const tagDeg = answers.q8_geometry.horizontalTagDeg;
      if (theta !== null && theta > 10) {
        bump('IV');
        qeEngaged = true;
        triggers.push({ rule: 'IV(f)', detail: `Straight tag with vertical load-position angle ${theta.toFixed(1)}° exceeding 10° (§4.4.4).` });
      }
      if (tagDeg !== null && tagDeg > 70) {
        bump('IV');
        qeEngaged = true;
        triggers.push({ rule: 'IV(f)', detail: `Straight tag with horizontal tag angle ${tagDeg.toFixed(1)}° exceeding 70° (§4.4.4).` });
      }
    }
  }

  // Override: QP may select any class; downgrade (less restrictive) requires
  // a non-empty justification — never trusted without one (§4 override rule).
  let overrideRecord: ClassificationResult['override'] = null;
  let selected: ConstructionClass = recommended;
  if (override) {
    selected = override.selected;
    if (CLASS_RANK[selected] < CLASS_RANK[recommended]) {
      const justification = override.justification?.trim();
      if (!justification) {
        throw new ClassificationOverrideError(
          `Selected class ${selected} is less restrictive than recommended class ${recommended} — ` +
          'a written override justification is required (A10.48-2023 §4.4.4.1). ' +
          'Conditions in question ⇒ engage QE per ANSI/TIA-322-A.'
        );
      }
      overrideRecord = { by: override.by, justification, timestamp: override.timestamp ?? now };
    } else if (selected !== recommended) {
      // Upgrade (more restrictive) — allowed, recorded without justification.
      overrideRecord = {
        by: override.by,
        justification: override.justification?.trim() || `Selected ${selected} (more restrictive than recommended ${recommended}).`,
        timestamp: override.timestamp ?? now,
      };
    }
  }

  return {
    recommended,
    selected,
    engineVersion: '1.0.0',
    inputs: {
      answers,
      liftGeometrySnapshot: liftDetails.map((l) => ({
        grossLoadLbs: l.loading?.grossLoadLbs ?? null,
        tagMethod: l.geometry?.tag?.method ?? null,
        topBlockAttachment: l.geometry?.topBlock?.attachment ?? null,
        highestAttachmentFt: l.geometry?.highestAttachmentFt ?? null,
      })),
    },
    triggers,
    exception: {
      invoked: exceptionInvoked,
      conditions: exceptionConditions,
      conditionsMeta: {},
      qeEngaged,
    },
    override: overrideRecord,
    requiredPersonnel: requiredPersonnelFor(selected),
  };
}

/** §4.4.4.1(b) auto-suggest rule: surface the exception card (pre-check nothing). */
export function shouldSurfaceExceptionCard(grossLoadLbs: number | null, attachmentDeltaFt: number | null): boolean {
  return grossLoadLbs !== null && attachmentDeltaFt !== null && grossLoadLbs <= 200 && attachmentDeltaFt <= 4;
}

function liftLabel(lift: ClassifierLiftDetail): string {
  const withId = lift as ClassifierLiftDetail & { id?: string; name?: string };
  return withId.name ?? withId.id ?? 'lift';
}

function formatLbs(lbs: number): string {
  return `${lbs.toLocaleString('en-US')} lb`;
}
